import { z } from 'zod';
import type {
  EmbedFrameKind,
  EmbedLifecycleEvent,
  EmbedProtocolContext,
} from '@responsedock/types';
import { embedThemeSchema } from './embed-theme.js';

const commandBase = {
  protocol: z.literal('responsedock.embed'),
  version: z.literal(1),
};

export const embedHostCommandSchema = z.discriminatedUnion('type', [
  z.object({ ...commandBase, type: z.literal('responsedock.command.ping'), requestId: z.string().max(120).optional() }).strict(),
  z.object({ ...commandBase, type: z.literal('responsedock.command.theme.update'), theme: embedThemeSchema }).strict(),
  z.object({ ...commandBase, type: z.literal('responsedock.command.locale.update'), locale: z.string().regex(/^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/).max(35) }).strict(),
  z.object({ ...commandBase, type: z.literal('responsedock.command.navigate'), path: z.string().regex(/^\/(?!\/)[^\s]*$/).max(500) }).strict(),
  z.object({ ...commandBase, type: z.literal('responsedock.command.close'), reason: z.string().max(200).optional() }).strict(),
]);

export type ParsedEmbedHostCommand = z.infer<typeof embedHostCommandSchema>;

let sequence = 0;

function messageId(): string {
  sequence += 1;
  return `rd_${Date.now().toString(36)}_${sequence.toString(36)}`;
}

export function createEmbedLifecycleEvent<T extends EmbedLifecycleEvent['type']>(
  type: T,
  payload: Extract<EmbedLifecycleEvent, { type: T }>['payload'],
  context: EmbedProtocolContext,
): Extract<EmbedLifecycleEvent, { type: T }> {
  return {
    source: 'responsedock',
    protocol: 'responsedock.embed',
    version: 1,
    type,
    messageId: messageId(),
    timestamp: new Date().toISOString(),
    context,
    payload,
  } as Extract<EmbedLifecycleEvent, { type: T }>;
}

export function embedFrameContext(frame: EmbedFrameKind, values: Omit<EmbedProtocolContext, 'frame'> = {}): EmbedProtocolContext {
  return { frame, ...values };
}

